import { countLabel } from '@/utils/plural'
import { PublicSectionGate } from '@components/PublicPage'
import EmptyState from '@components/EmptyState'
import { usePublicIdeas, usePublicOverview } from '@hooks'
import { EventCategory, GetIdeaResponse } from '@orbit/api-types'
import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { filterIdeas } from '../Ideas/ideaFilters'
import { IdeaFilterRow, IdeaTileGrid } from '../Ideas/IdeaGrid'
import PublicIdeaModal from './PublicIdeaModal'
import { buildTravelerLookup, travelerFullName } from './publicMembers'
import PublicSectionLayout from './PublicSectionLayout'

/**
 * Public, read-only ideas at `/shared/trips/:tripCode/ideas`. Renders the same
 * flat grid of square tiles and the same category/location dropdowns as the
 * member ideas page (both come from `../Ideas/IdeaGrid`, so the two surfaces
 * can't drift) — a tile here opens a read-only popup instead of the edit form.
 * Gating (loading / 404-unavailable / transient error) is the shared
 * `PublicSectionGate`.
 */
const PublicIdeasPage: React.FC = () => {
	const { tripCode } = useParams<{ tripCode: string }>()
	const query = usePublicIdeas(tripCode)
	const [selectedLocation, setSelectedLocation] = useState<string | null>(null)
	const [selectedCategory, setSelectedCategory] = useState<EventCategory | null>(null)
	const [viewerIdea, setViewerIdea] = useState<GetIdeaResponse | null>(null)

	// Creator names resolve from the overview's travelers (already cached from
	// the layout's overview query); until it lands the popup omits "Added by".
	const { data: overview } = usePublicOverview(tripCode)
	const travelerLookup = buildTravelerLookup(overview?.travelers ?? [])

	return (
		<PublicSectionGate
			query={query}
			loadingLabel="Loading ideas…"
			unavailableTitle="Ideas Unavailable"
			unavailableMessage="These ideas don't exist or haven't been shared publicly by the trip owner."
		>
			{data => {
				const ideas = data.ideas
				const visibleIdeas = filterIdeas(ideas, {
					location: selectedLocation,
					category: selectedCategory,
				})
				return (
					<PublicSectionLayout
						// Data present implies the query ran, which implies the code param existed.
						tripCode={tripCode ?? ''}
						title="Ideas"
						subtitle={`${countLabel(ideas.length, 'idea')}`}
					>
						{ideas.length === 0 ? (
							<EmptyState
								art="ideas"
								title="No ideas yet"
								description="No ideas have been added to this trip yet."
							/>
						) : (
							<>
								<IdeaFilterRow
									ideas={ideas}
									category={selectedCategory}
									location={selectedLocation}
									onCategoryChange={setSelectedCategory}
									onLocationChange={setSelectedLocation}
								/>
								<IdeaTileGrid ideas={visibleIdeas} onSelect={setViewerIdea} />
							</>
						)}

						<PublicIdeaModal
							idea={viewerIdea}
							addedBy={
								viewerIdea
									? travelerFullName(travelerLookup(viewerIdea.createdByUserId))
									: null
							}
							onClose={() => setViewerIdea(null)}
						/>
					</PublicSectionLayout>
				)
			}}
		</PublicSectionGate>
	)
}

export default PublicIdeasPage
